/**
 * JARVIS voice: push-to-talk speech recognition (Web Speech API) and
 * read-aloud. Read-aloud uses the free in-browser neural voice (neuralVoice.js)
 * once its model is loaded, and the browser's speechSynthesis until then or
 * where it is unsupported. Both degrade to "unsupported" quietly — Firefox has
 * no SpeechRecognition.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { neuralSupported, neuralReady, preloadNeural, readEngine, speakNeural, stopNeural } from './neuralVoice';

const PREF_KEY = 'jarvis.speaker';

export function readSpeakerPref() {
  try { return window.localStorage.getItem(PREF_KEY) === '1'; } catch (e) { return false; }
}
export function writeSpeakerPref(on) {
  try { window.localStorage.setItem(PREF_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ }
}

const browserSynthesis = () => typeof window !== 'undefined' && 'speechSynthesis' in window
  && typeof window.SpeechSynthesisUtterance === 'function';
/** True when JARVIS can read replies aloud by any engine. */
export const synthesisSupported = () => browserSynthesis() || neuralSupported();

const recognitionCtor = () => (typeof window === 'undefined' ? null
  : window.SpeechRecognition || window.webkitSpeechRecognition || null);
export const recognitionSupported = () => !!recognitionCtor();

/** Markdown → plain speakable text. */
export function stripMarkdown(md) {
  return String(md || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(\*|_)(.+?)\1/g, '$2')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/https?:\/\/\S+/g, 'the link')
    .replace(/\s+/g, ' ')
    .trim();
}

const MALE_HINT = /(daniel|arthur|george|ryan|guy|david|mark|james|thomas|oliver|fred|alex|male)/i;
let cachedVoice = null;
function pickVoice() {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis.getVoices() || [];
  const en = voices.filter((v) => /^en[-_]/i.test(v.lang) || v.lang === 'en');
  cachedVoice = en.find((v) => /en[-_]GB/i.test(v.lang) && MALE_HINT.test(v.name))
    || en.find((v) => MALE_HINT.test(v.name))
    || en.find((v) => /en[-_]GB/i.test(v.lang))
    || en[0] || null;
  return cachedVoice;
}

export function stopSpeaking() {
  stopNeural();
  if (browserSynthesis()) window.speechSynthesis.cancel();
}

/** Start loading the neural voice if it is the chosen engine. */
export function warmVoice() {
  if (readEngine() === 'neural') preloadNeural();
}

/** Speak markdown text; onEnd fires when finished or cancelled. */
export function speak(markdown, onEnd) {
  const text = stripMarkdown(markdown);
  if (!text) { if (onEnd) onEnd(); return; }
  stopSpeaking();
  if (readEngine() === 'neural') {
    if (neuralReady()) { speakNeural(text, onEnd); return; }
    preloadNeural(); // ready for the next reply; the browser voice covers this one
  }
  if (!browserSynthesis()) { if (onEnd) onEnd(); return; }
  const u = new window.SpeechSynthesisUtterance(text.slice(0, 2400));
  const v = pickVoice();
  if (v) { u.voice = v; u.lang = v.lang; }
  u.rate = 1.04;
  u.pitch = 0.95;
  u.onend = () => onEnd && onEnd();
  u.onerror = () => onEnd && onEnd();
  window.speechSynthesis.speak(u);
}

if (browserSynthesis() && typeof window.speechSynthesis.addEventListener === 'function') {
  // Voices load asynchronously in Chrome; re-pick when they arrive.
  window.speechSynthesis.addEventListener('voiceschanged', () => { cachedVoice = null; });
}

/**
 * Push-to-talk recognition. start() on press, stop() on release; onFinal is
 * called with the final transcript when recognition ends.
 */
export function useSpeechRecognition({ onFinal, onStart, onEnd }) {
  const [interim, setInterim] = useState('');
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const finalRef = useRef('');
  const cbRef = useRef({ onFinal, onStart, onEnd });
  cbRef.current = { onFinal, onStart, onEnd };

  const supported = recognitionSupported();

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor || recRef.current) return;
    const rec = new Ctor();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = true;
    finalRef.current = '';
    setInterim('');
    setError(null);
    rec.onresult = (e) => {
      let fin = '';
      let mid = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) fin += r[0].transcript; else mid += r[0].transcript;
      }
      finalRef.current = fin;
      setInterim((fin + ' ' + mid).trim());
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setError('Microphone access was blocked.');
      else if (e.error !== 'aborted' && e.error !== 'no-speech') setError('Voice input failed — try again.');
    };
    rec.onend = () => {
      recRef.current = null;
      setActive(false);
      const text = (finalRef.current || '').trim();
      setInterim('');
      if (cbRef.current.onEnd) cbRef.current.onEnd();
      if (text && cbRef.current.onFinal) cbRef.current.onFinal(text);
    };
    recRef.current = rec;
    try {
      rec.start();
      setActive(true);
      if (cbRef.current.onStart) cbRef.current.onStart();
    } catch (e) {
      recRef.current = null;
      setError('Voice input is unavailable.');
    }
  }, []);

  const stop = useCallback(() => {
    if (recRef.current) {
      // Give the engine a beat to finalize the last words before stopping.
      const rec = recRef.current;
      setTimeout(() => { try { rec.stop(); } catch (e) { /* already stopped */ } }, 250);
    }
  }, []);

  useEffect(() => () => { if (recRef.current) { try { recRef.current.abort(); } catch (e) { /* noop */ } } }, []);

  return { supported, active, interim, error, start, stop };
}
